<template>
    <div class="container">
        <div class="mb-10 d-flex justify-content-between align-items-end gap-20">
            <v-expansion-panels>
                <v-expansion-panel style="background-color: #f5f5f5;">
                    <v-expansion-panel-header>
                        <div class="table-title">
                            Utiliser un modèle d'e-mails
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-alert text class="mb-5" type="info" border="right" colored-border>
                            Pour faciliter la saisie des contenus d'e-mails envoyés aux professionnels, vous pouvez
                            créer un modèle d'e-mail <router-link to="/configuration/email-template" target="_blank">ici</router-link>
                            .<br>
                            Vous pourrez ensuite le sélectionner dans la liste ci-dessous pour précharger les champs
                            <span class="email-content-span">Objet</span>,
                            <span class="email-content-span">Première partie de l'e-mail</span> et
                            <span class="email-content-span">Dernière partie de l'e-mail</span>.
                        </v-alert>

                        <div class="d-flex mb-5">
                            <div class="mt-auto mb-auto mr-3">
                                <v-label>Charger un modèle d'e-mail</v-label>
                            </div>

                            <v-select hide-details outlined :items="templates" @input="loadTemplate" item-text="name"
                                return-object style="padding-top: 0!important;">
                            </v-select>

                            <div class="ml-2 mt-auto mb-auto">
                                <v-tooltip bottom >
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon class="m-2"
                                                    id="updateBtn"
                                                    @click="getTemplates">
                                                refresh
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Rafraichir la liste de vos modèles d'e-mails</span>
                                </v-tooltip>
                            </div>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>

        <h3><span class="required">Émetteur</span></h3>
        <v-select :items="senders" outlined class="no-details mb-5" item-text="emailable.email" @change="selectSender"
            style="padding-top: 0!important;" return-object :value="sender">
            <v-tooltip slot="append-outer" bottom>
                <template v-slot:activator="{ on }">
                    <v-icon v-on="on">help</v-icon>
                </template>
                <span>Adresse E-mail et Nom d'expéditeur que verront vos prestataires.</span>
            </v-tooltip>

            <template slot="selection" slot-scope="data">
                <span v-if="data.item.name !== null" class="pr-2">
                    {{ data.item.name }}
                </span>
                <span>({{ data.item.emailable.email }})</span>
            </template>
            <template slot="item" slot-scope="data">
                <span v-if="data.item.name !== null" class="pr-2">
                    {{ data.item.name }}
                </span>
                <span>({{ data.item.emailable.email }})</span>
            </template>
        </v-select>

        <h3><span class="required">Objet</span></h3>
        <v-text-field background-color="white" outlined style="padding-top: 0!important;" :value="subject"
            @change="updateSubject" class="subject-input">
        </v-text-field>

        <v-alert text class="mb-5 mt-5" type="info">
            Pour générer un objet en fonction du nom ou du type de prestataire, vous pouvez utiliser les tags suivants
            :<br>
            <b>#nom#</b> : le nom de la fiche<br>
            <b>#type#</b> : le type de la fiche (ex: Restauration)
        </v-alert>

        <h3><span class="required">Première partie de l'e-mail</span></h3>
        <ckeditor :editor="editor" :value="firstPartEmail" @input="updateFirstPartEmail"></ckeditor>

        <div class="text-center valid-button">
            <form-link :generated="true" :name="firstProvider.name" :adhesionWording="adhesionWording"></form-link>
        </div>

        <h3><span class="required">Dernière partie de l'e-mail</span></h3>
        <ckeditor :editor="editor" :value="lastPartEmail" @input="updateLastPartEmail"></ckeditor>

        <div :class="{ 'disabled-box': sender === null }">
            <h3 class="mt-5" style="margin-bottom: 0 !important;">Signature</h3>
            <div class="d-flex align-items-center">
                <div class="mt-auto mb-auto">
                    <v-checkbox v-model="generateSignature" label="Générer automatiquement la signature" hide-details
                        style="padding-top: 0 !important;" class="mr-3 mt-0 pt-0">
                    </v-checkbox>
                </div>

                <div class="mt-auto mb-auto">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-icon v-on="on">help</v-icon>
                        </template>
                        <span>
                            En cochant cette case, la signature de l'émetteur sera utilisée (voir exemple ci-contre)
                        </span>
                    </v-tooltip>
                </div>
            </div>

            <div class="mt-5 valid-button mb-0" v-if="generateSignature">
                <div class="title-valid-button">Signature</div>

                <div class="mt-3">
                    <v-progress-circular indeterminate color="primary" v-if="loadSignature"></v-progress-circular>
                    <div v-else v-html="signature"></div>
                </div>
            </div>

            <div v-else class="mt-5">
                <ckeditor :editor="editor" @input="updateSignature" :value="signature"></ckeditor>
            </div>
        </div>

        <v-dialog v-model="preview" persistent max-width="600px" content-class="preview-modal">
            <v-card>
                <v-card-title>
                    <div class="w-100 d-flex justify-content-between align-items-center">
                        <span class="headline">Prévisualisation de l'email</span>
                        <v-icon @click="preview = false">clear</v-icon>
                    </div>
                </v-card-title>
                <v-card-text>
                    <div class="mb-4">
                        <b>{{ generatedSubject }}</b>
                    </div>

                    <div v-html="firstPartEmail" class="mb-3"></div>
                    <div class="valid-button text-center">
                        <form-link :name="firstProvider.name" :adhesionWording="adhesionWording"></form-link>
                    </div>
                    <div v-html="lastPartEmail"></div>
                    <div class="mt-5" v-html="signature"></div>
                </v-card-text>

                <!-- <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="error" @click="preview = false">Fermer</v-btn>
                </v-card-actions> -->
            </v-card>
        </v-dialog>

        <div class="d-flex justify-content-end gap-20 mb-5 mt-5">
            <v-btn class="custom-button-primary" outlined @click="preview = true">
                <v-icon class="mr-2">remove_red_eye</v-icon> Voir l'e-mail
            </v-btn>
            <v-btn class="default-button" @click="setCurrentStep(parseInt(currentStep) + 1)">Passer à l'étape suivante
            </v-btn>
        </div>
    </div>
</template>

<script>
import ClassicEditor from '../../../../plugins/ckeditor'
import FormLink from "@/components/campaign/cadh/FormLink"
import { mapState, mapMutations } from 'vuex'

export default {
    name: "Email",
    components: { FormLink },
    data() {
        return {
            editor: ClassicEditor,
            generateSignature: false,
            currentUser: {},
            logo: {},
            preview: false,
            loadSignature: false,
            firstProvider: {},
            templates: [],
            adhesionWording: 'adhésion',
        }
    },
    methods: {
        ...mapMutations('createCadh', [
            'setSender',
            'setReceiver',
            'setSubject',
            'setFirstPartMail',
            'setLastPartMail',
            'setSignature',
            'setCurrentStep',
        ]),
        selectSender(sender) {
            this.setSender(sender)
        },
        selectReceiver(receiver) {
            this.setReceiver(receiver)
        },
        updateSubject(subject) {
            this.setSubject(subject)
        },
        updateFirstPartEmail(text) {
            this.setFirstPartMail(text)
        },
        updateLastPartEmail(text) {
            this.setLastPartMail(text)
        },
        updateSignature(text) {
            this.setSignature(text)
        },
        getSignature() {
            if (this.sender !== null) {
                this.loadSignature = true

                this.$http.get('auth/user/signature/' + this.sender.id).then(res => {
                    this.updateSignature(res.body.data.signature)

                    this.loadSignature = false
                })
            }
        },
        getTemplates() {
            let btn = document.getElementById('updateBtn')

            if (btn) {
                btn.classList.add('fa-spin')
            }

            this.$http.get('auth/account/email-templates').then(res => {
                this.templates = res.body.data.templates

                if (btn) {
                    setTimeout(() => {
                        btn.classList.remove('fa-spin')
                    }, 500)
                }

            })
        },
        loadTemplate(template) {
            this.updateSubject(template.subject)
            this.updateFirstPartEmail(template.part1)
            this.updateLastPartEmail(template.part2)
        },
        getAdhesionWording() {
            this.$http.get("auth/campaigns/cadh/cadh_config_partenariat/" + this.cadhConfigId).then(res => {
                this.adhesionWording = res.body.data.adhesionWording
            })
        },
    },
    computed: {
        ...mapState('createCadh', [
            'sender',
            'receiver',
            'subject',
            'firstPartEmail',
            'lastPartEmail',
            'signature',
            'providers',
            'currentStep',
            "cadhConfigId",
        ]),
        ...mapState('account', ['users', 'senders', 'token']),
        generatedSubject() {
            let subject = this.subject

            subject = subject.replace('#nom#', this.firstProvider.name)
            subject = subject.replace('#type#', this.firstProvider.category)

            return subject
        }
    },
    watch: {
        generateSignature() {
            if (this.generateSignature) {
                this.getSignature()
            }
        },
        cadhConfigId() {
            if(this.cadhConfigId) {
                this.getAdhesionWording()
            }
        },
    },
    created() {
        this.firstProvider = this.providers[0] !== undefined ? this.providers[0] : {'name': 'Nom du prestataire', 'category': 'Catégorie du prestataire'}

        this.getTemplates()

        this.editor.defaultConfig.simpleUpload.headers = {
            Authorization: 'Bearer ' + this.token
        }

        if(this.cadhConfigId) {
            this.getAdhesionWording()
        }
    }
}
</script>

